/**
 * Despacho a domicilio o retiro en el local (CHK-03).
 *
 * Se muestran SOLO las formas que el panel deja encendidas
 * (`OpcionesDespacho.entregas`, resuelta por el backend): las dos, una, o
 * ninguna. Con ninguna no hay nada que elegir: el comprador paga los productos
 * y un aviso le dice que la entrega se acuerda despues por telefono, con el
 * numero del negocio a mano.
 *
 * Las opciones son radios dentro de un `fieldset`: es un grupo de una sola
 * eleccion, y el teclado tiene que recorrerlo con las flechas y no con quince
 * tabulaciones. La direccion aparece SOLO con despacho — pedirle la calle a
 * quien va a retirar es fricción por nada.
 *
 * El precio de cada opcion se ve antes de elegirla, que es lo que `ENV-01` pide:
 * saber el total real antes de pagar, no despues.
 *
 * Region y comuna son dos selectores encadenados y no un texto libre
 * (`SelectorRegionComuna`): el costo puede cambiar por comuna (ADM-10).
 */
import type { OpcionesDespacho } from '@bandu/compartido';

import { SITIO } from '../../configuracion/sitio';
import type { DatosCheckout, ErroresCheckout } from '../../utilidades/checkout';
import { comunaElegida, regionElegida } from '../../utilidades/checkout';
import { ofreceDespacho, ofreceRetiro, soloCoordinar } from '../../utilidades/entrega';
import { pesos } from '../../utilidades/formato';
import { Cargando } from '../comunes/EstadoPeticion';
import { Campo } from './Campo';
import { SelectorRegionComuna } from './SelectorRegionComuna';

interface Props {
  datos: DatosCheckout;
  errores: ErroresCheckout;
  opciones: OpcionesDespacho | null;
  subtotal: number;
  alCambiar: (cambio: Partial<DatosCheckout>) => void;
  alSalirDelCampo: (campo: keyof DatosCheckout) => void;
}

export function PanelEntrega({
  datos,
  errores,
  opciones,
  subtotal,
  alCambiar,
  alSalirDelCampo,
}: Props) {
  const region = regionElegida(datos, opciones);
  const comuna = comunaElegida(datos, opciones);
  const gratis = opciones !== null && subtotal >= opciones.despachoGratisDesde;
  const conDespacho = ofreceDespacho(opciones);
  const conRetiro = ofreceRetiro(opciones);

  // Sin comuna elegida se muestra el costo base de la region, y sin region el
  // rango del catalogo entero: un numero siempre, para no dejar el resumen en
  // blanco mientras se completa el formulario.
  const costoDespacho = comuna?.costo ?? region?.costo ?? null;

  return (
    <section className="panel" aria-labelledby="ck-entrega">
      <div className="panel-cab">
        <h2 id="ck-entrega">Entrega</h2>
      </div>

      <div className="panel-cuerpo">
        {opciones === null ? <Cargando que="las formas de entrega" /> : null}

        {soloCoordinar(opciones) ? (
          <div className="entrega-coordinar">
            <p className="t">La entrega la coordinamos contigo</p>
            <p>
              Ahora pagas solo los productos. Después te llamamos al teléfono que nos dejes para
              acordar cómo y cuándo te llega tu pedido, y su costo. Si prefieres, llámanos o
              escríbenos al{' '}
              <a href={`tel:+${SITIO.whatsapp}`}>{SITIO.whatsappVisible}</a>.
            </p>
          </div>
        ) : null}

        {conDespacho || conRetiro ? (
          <fieldset>
            <legend className="solo-lectores">Como quieres recibir tu pedido</legend>

            {conDespacho ? (
              <label className={`opcion-envio${datos.tipoEntrega === 'despacho' ? ' sel' : ''}`}>
                <input
                  type="radio"
                  name="entrega"
                  value="despacho"
                  checked={datos.tipoEntrega === 'despacho'}
                  onChange={() => alCambiar({ tipoEntrega: 'despacho' })}
                />
                <span className="opcion-texto">
                  <span className="t">Despacho a domicilio</span>
                  <span className="d">
                    {region !== undefined
                      ? `${comuna?.plazo ?? region.plazo} · ${region.nombre}`
                      : 'Elige tu region para ver el costo y el plazo'}
                  </span>
                </span>
                <span className={`pr${gratis ? ' gratis' : ''}`}>
                  {gratis ? 'Gratis' : costoDespacho !== null ? pesos(costoDespacho) : '—'}
                </span>
              </label>
            ) : null}

            {conRetiro ? (
              <label className={`opcion-envio${datos.tipoEntrega === 'retiro' ? ' sel' : ''}`}>
                <input
                  type="radio"
                  name="entrega"
                  value="retiro"
                  checked={datos.tipoEntrega === 'retiro'}
                  onChange={() => alCambiar({ tipoEntrega: 'retiro' })}
                />
                <span className="opcion-texto">
                  <span className="t">Retiro en la sala de ventas</span>
                  <span className="d">
                    {`${SITIO.direccion.calle}, ${SITIO.direccion.comuna} · ${opciones?.plazoRetiro ?? ''}`}
                  </span>
                </span>
                <span className="pr gratis">Gratis</span>
              </label>
            ) : null}
          </fieldset>
        ) : null}

        {datos.tipoEntrega === 'despacho' && conDespacho ? (
          <div className="detalle-entrega">
            <SelectorRegionComuna
              prefijoId="ck"
              seccion="shipping"
              regiones={opciones?.regiones ?? []}
              comunas={region?.comunas.map((opcion) => opcion.nombre)}
              codigoRegion={datos.codigoRegion}
              comuna={datos.comuna}
              errorRegion={errores.codigoRegion}
              errorComuna={errores.comuna}
              // Cambiar de region invalida la comuna: la de antes ya no
              // pertenece a la nueva y cobraria una tarifa que no le toca.
              alCambiarRegion={(codigo) => alCambiar({ codigoRegion: codigo, comuna: '' })}
              alCambiarComuna={(nombre) => alCambiar({ comuna: nombre })}
              alSalirRegion={() => alSalirDelCampo('codigoRegion')}
              alSalirComuna={() => alSalirDelCampo('comuna')}
            />

            <div className="fila-campos">
              <Campo id="ck-calle" etiqueta="Calle" error={errores.calle}>
                {(atributos) => (
                  <input
                    {...atributos}
                    type="text"
                    autoComplete="address-line1"
                    value={datos.calle}
                    onChange={(evento) => alCambiar({ calle: evento.target.value })}
                    onBlur={() => alSalirDelCampo('calle')}
                  />
                )}
              </Campo>

              <Campo id="ck-numero" etiqueta="Numero" error={errores.numero}>
                {(atributos) => (
                  <input
                    {...atributos}
                    type="text"
                    value={datos.numero}
                    onChange={(evento) => alCambiar({ numero: evento.target.value })}
                    onBlur={() => alSalirDelCampo('numero')}
                  />
                )}
              </Campo>
            </div>

            <div className="fila-campos">
              <Campo id="ck-departamento" etiqueta="Departamento u oficina (opcional)">
                {(atributos) => (
                  <input
                    {...atributos}
                    type="text"
                    autoComplete="address-line2"
                    value={datos.departamento}
                    onChange={(evento) => alCambiar({ departamento: evento.target.value })}
                  />
                )}
              </Campo>

              <Campo
                id="ck-referencia"
                etiqueta="Referencia (opcional)"
                ayuda="Un dato que le sirva a quien despacha: entre calles, color del porton."
              >
                {(atributos) => (
                  <input
                    {...atributos}
                    type="text"
                    value={datos.referencia}
                    onChange={(evento) => alCambiar({ referencia: evento.target.value })}
                  />
                )}
              </Campo>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
